import { useEffect, useState } from "react";
import { endpoints, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { formatCurrency } from "../lib/romEngine";
import { X, Clock, CircleNotch, PaperPlaneTilt, CurrencyDollar } from "@phosphor-icons/react";

const todayISO = () => new Date().toISOString().slice(0, 10);

/**
 * Quick "Log time" modal pre-linked to a Job/ROM (`job`) or Project (`project`).
 * Shows the current labour roll-up and (for jobs, manager/admin) a one-click
 * "Record approved labour as actual" that feeds ROM-vs-actual variance.
 */
export default function QuickLogTimeModal({ job, project, onClose, onChanged }) {
    const { user } = useAuth();
    const isManage = ["admin", "manager"].includes(user?.role);
    const target = job ? { kind: "job", id: job.id, label: job.ref } : { kind: "project", id: project.id, label: project.name };

    const [employees, setEmployees] = useState([]);
    const [roll, setRoll] = useState(null);
    const [form, setForm] = useState({ employee_id: "", work_date: todayISO(), decimal_hours: "", type_raw: "", notes_raw: "" });
    const [busy, setBusy] = useState(false);
    const [recording, setRecording] = useState(false);
    const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    async function loadRoll() {
        try {
            const { data } = job ? await endpoints.jobLabour(job.id) : await endpoints.projectLabour(project.id);
            setRoll(data);
        } catch { /* ignore */ }
    }
    useEffect(() => {
        endpoints.employees({ active_only: true }).then((r) => setEmployees(r.data || [])).catch(() => {});
        loadRoll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function submit() {
        if (!form.employee_id) { alert("Select an employee"); return; }
        const hours = parseFloat(form.decimal_hours);
        if (!hours || hours <= 0) { alert("Enter hours > 0"); return; }
        setBusy(true);
        try {
            await endpoints.createTimeEntry({
                employee_id: form.employee_id,
                work_date: form.work_date,
                entry_type: "work",
                type_raw: form.type_raw,
                job_id: job ? job.id : null,
                job_ref: job ? job.ref : "",
                project_id: project ? project.id : null,
                decimal_hours: hours,
                notes_raw: form.notes_raw,
            });
            setForm((f) => ({ ...f, decimal_hours: "", type_raw: "", notes_raw: "" }));
            await loadRoll();
            onChanged?.();
        } catch (e) { alert(formatApiError(e)); }
        finally { setBusy(false); }
    }

    async function recordAsActual() {
        if (!job || !roll?.approved_cost) return;
        if (!window.confirm(`Record ${formatCurrency(roll.approved_cost)} of approved labour as the Tier-B labour actual for ${job.ref}?`)) return;
        setRecording(true);
        try {
            await endpoints.recordActual(job.id, { tier: "B", actual_cost: roll.approved_cost, breakdown: { labour: roll.approved_cost } });
            alert("Labour recorded as actual — it now feeds ROM-vs-actual variance.");
            onChanged?.();
            onClose();
        } catch (e) { alert(formatApiError(e)); }
        finally { setRecording(false); }
    }

    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => !busy && onClose()}>
            <div className="bg-white rounded-lg w-full max-w-lg shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="quick-log-modal">
                <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--rom-border)]">
                    <h3 className="font-display font-bold text-lg flex items-center gap-2">
                        <Clock size={18} weight="bold" className="text-[var(--rom-orange)]" /> Log time · {target.label}
                    </h3>
                    <button onClick={onClose} className="p-1 text-[var(--rom-text-2)] hover:text-[var(--rom-text)]"><X size={18} /></button>
                </div>

                {roll && (
                    <div className="px-5 pt-4 grid grid-cols-3 gap-3" data-testid="labour-rollup">
                        <Roll label="Hours" value={`${roll.total_hours}h`} />
                        <Roll label="Labour cost" value={formatCurrency(roll.total_cost)} />
                        <Roll label="Approved" value={formatCurrency(roll.approved_cost)} sub={roll.pending ? `${roll.pending} pending` : "all approved"} />
                    </div>
                )}

                <div className="p-5 grid grid-cols-2 gap-4">
                    <div>
                        <label className={labelCls}>Employee *</label>
                        <select data-testid="ql-employee" value={form.employee_id} onChange={(e) => setF("employee_id", e.target.value)} className={inputCls}>
                            <option value="">Select…</option>
                            {employees.map((e) => <option key={e.id} value={e.id}>{e.employee_name}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className={labelCls}>Date *</label>
                        <input data-testid="ql-date" type="date" value={form.work_date} onChange={(e) => setF("work_date", e.target.value)} className={inputCls} />
                    </div>
                    <div>
                        <label className={labelCls}>Hours *</label>
                        <input data-testid="ql-hours" type="number" step="0.25" min="0.25" value={form.decimal_hours} onChange={(e) => setF("decimal_hours", e.target.value)} placeholder="8" className={inputCls} />
                    </div>
                    <div>
                        <label className={labelCls}>Work type</label>
                        <input data-testid="ql-type" value={form.type_raw} onChange={(e) => setF("type_raw", e.target.value)} placeholder="e.g. Drying setup" className={inputCls} />
                    </div>
                    <div className="col-span-2">
                        <label className={labelCls}>Notes</label>
                        <input data-testid="ql-notes" value={form.notes_raw} onChange={(e) => setF("notes_raw", e.target.value)} className={inputCls} />
                    </div>
                </div>

                <div className="px-5 pb-5 flex items-center gap-2 flex-wrap">
                    <button data-testid="ql-submit" onClick={submit} disabled={busy} className="btn btn-primary flex-1 justify-center">
                        {busy ? <CircleNotch size={14} className="animate-spin" /> : <PaperPlaneTilt size={14} weight="bold" />} Log (pending approval)
                    </button>
                    {job && isManage && roll?.approved_cost > 0 && (
                        <button data-testid="ql-record-actual" onClick={recordAsActual} disabled={recording} className="btn btn-ghost">
                            {recording ? <CircleNotch size={14} className="animate-spin" /> : <CurrencyDollar size={14} weight="bold" />} Record labour as actual
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

const inputCls = "w-full h-9 px-3 rounded-[4px] border border-[var(--rom-border)] text-sm focus:border-[var(--rom-orange)] focus:outline-none bg-white";
const labelCls = "block text-xs font-semibold text-[var(--rom-text-2)] mb-1";

function Roll({ label, value, sub }) {
    return (
        <div className="border border-[var(--rom-border)] rounded-[6px] p-3 text-center">
            <div className="text-[10px] uppercase tracking-wider text-[var(--rom-text-3)] font-semibold">{label}</div>
            <div className="font-display font-black text-lg mt-0.5">{value}</div>
            {sub && <div className="text-[10px] text-[var(--rom-text-3)]">{sub}</div>}
        </div>
    );
}
